import { macroCondition, isTesting } from '@embroider/macros';
import Button from 'tio-ui/components/button';
import { concat } from '@ember/helper';
import { and } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { PhUserSwitch, PhX } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import { insertReviewerRequest } from 'tio-data/requests/tas-program-instance';
import { insertReviewerRequest as insertTasApplicationReviewerRequest } from 'tio-data/requests/tas-application-actions';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import Select from 'tio-ui/components/select';
import { task } from 'reactiveweb/ember-concurrency';
import { trackedFunction } from 'reactiveweb/function';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../../types/tuition-assistance';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstance } from 'tio-data/data/resources/tas-program-instance.ext';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { query } from '@warp-drive/utilities/json-api';
import { lookupEmployeesByIds } from '../../../../utils/employee-lookup';
import { reloadRecord } from 'tio-data/requests/save-record';

const INPUT_DEBOUNCE = macroCondition(isTesting()) ? 0 : 250;

// Automated approvers carry no employee; the chain shows their role and nothing else.
function isSystemApprover(dot: string): boolean {
  return dot === 'TAS.Approver.TIO_APPROVER' || dot === 'TAS.Approver.TIO_DEADLINE_APPROVER';
}

/**
 * Approval Queue Status drawer component
 * Displays approval workflow with better visual hierarchy
 * Includes inline reassignment UI to avoid modal/drawer conflicts
 */
export interface ApprovalQueueStatusDrawerSignature {
  Args: {
    application: TasApplicationModel | TasProgramInstance;
    consumingApp: string;
  };
}

export default class ApprovalQueueStatusDrawer extends Component<ApprovalQueueStatusDrawerSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  // Reassignment state - now owned by the drawer
  @tracked selectedApprover: ApplicationApprovalSummaryApprover | null = null;
  @tracked showReassignmentUI = false;
  @tracked selectedApproverId = '';
  @tracked searchTerm = '';

  /**
   * Load employee names for all approvers via OpenSearch Person batch lookup.
   * Requires company_id filter to satisfy SearchPolicy for all token roles.
   */
  employeeDataState = trackedFunction(this, async () => {
    const allApprovers = [...this.approvalQueue.affirmed, ...this.approvalQueue.possible_reviewers];

    const employeeIds = Array.from(
      new Set(
        allApprovers
          .map((approver) => approver.employee_id)
          .filter((id): id is number => typeof id === 'number')
      )
    );

    if (!employeeIds.length) {
      return new Map<string, string>();
    }

    try {
      const results = await lookupEmployeesByIds(this.store, employeeIds, this.companyId);
      const nameMap = new Map<string, string>();
      for (const [id, person] of results) {
        nameMap.set(id, person.fullName || `Employee #${id}`);
      }
      return nameMap;
    } catch {
      return new Map<string, string>();
    }
  });

  getApproverName = (approverId: number | null): string => {
    const idString = String(approverId);

    if (this.employeeDataState.isLoading) {
      return `${this.intl.t('tuition_assistance.approval_queue.loading')}...`;
    }

    return (
      this.employeeDataState.value?.get(idString) ||
      this.intl.t('tuition_assistance.approval_queue.employee_fallback', { id: approverId ?? '' })
    );
  };

  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    switch (application.state) {
      case 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL':
        return application.evidenceApproval;
      case 'TAS.ApplicationState.PENDING_COURSES_APPROVAL':
        return application.coursesApproval;
      case 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL':
        return application.programApproval;
      case 'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL':
        return application.completionApproval;
      case 'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL':
        return application.withdrawalApproval;
      default:
        return {
          affirmed: [],
          current_reviewers: [],
          possible_reviewers: [],
          required: [],
          approved: false,
        };
    }
  }

  get affirmedReviewers() {
    return this.approvalQueue.affirmed;
  }

  get pendingReviewersByLevel() {
    const { affirmed, possible_reviewers } = this.approvalQueue;

    let tioApproverFound = false;

    const pendingReviewersQueue = possible_reviewers.filter((reviewer) => {
      const hasAlreadyApproved =
        reviewer.employee_id != null &&
        affirmed.some((approved) => approved.employee_id === reviewer.employee_id);
      const hasMatchingOrdinal = affirmed.some((approved) => approved.ordinal === reviewer.ordinal);

      if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
        if (tioApproverFound) {
          return false;
        } else {
          tioApproverFound = true;
        }
      }
      return !hasAlreadyApproved && !hasMatchingOrdinal;
    });

    return this.groupReviewersByOrdinal(pendingReviewersQueue);
  }

  groupReviewersByOrdinal(reviewers: ApplicationApprovalSummaryApprover[]) {
    const minOrdinal = Math.min(...reviewers.map((reviewer) => reviewer.ordinal));

    const groupedReviewers = reviewers.reduce<Record<number, ApplicationApprovalSummaryApprover[]>>(
      (result, reviewer) => {
        const { ordinal } = reviewer;
        const normalizedOrdinal = ordinal - minOrdinal;

        if (!(normalizedOrdinal in result)) {
          result[normalizedOrdinal] = [];
        }
        result[normalizedOrdinal]?.push(reviewer);

        return result;
      },
      {}
    );

    return groupedReviewers;
  }

  get companyId(): string | undefined {
    const application = this.args.application;
    const id =
      application.modelName === 'tas-program-instance'
        ? application.employee.company.id
        : application.tasProgramInstance.employee.company.id;
    return id ?? undefined;
  }

  getLevelNumber = (reviewers: ApplicationApprovalSummaryApprover[]) => {
    return reviewers[0]?.ordinal ?? 0;
  };

  handleReassignClick = (approver: ApplicationApprovalSummaryApprover) => {
    // Update local state to show reassignment UI
    this.selectedApprover = approver;
    this.showReassignmentUI = true;
  };

  cancelReassignment = () => {
    // Clear local reassignment state
    this.selectedApprover = null;
    this.showReassignmentUI = false;
    this.selectedApproverId = '';
    this.searchTerm = '';
  };

  onFilterChange = (term: string) => {
    this.searchTerm = term;
  };

  handleSelectionChange = async (key: string | null) => {
    this.selectedApproverId = key || '';
  };

  insertReviewer = dropTask(async () => {
    if (!this.selectedApproverId) return;
    const ordinal = this.selectedApprover!.ordinal;
    const employeeId = Number(this.selectedApproverId);

    if (this.args.application.modelName === 'tas-application') {
      await this.store.request(
        insertTasApplicationReviewerRequest(this.args.application.id as string, employeeId, ordinal)
      );
      return reloadRecord(this.store, this.args.application);
    }

    await this.store.request(
      insertReviewerRequest(this.args.application.id as string, employeeId, ordinal)
    );
    // Legacy adapter#insertReviewer reloaded the program-instance before
    // returning so the parent saw the new approver chain. The builder
    // shape is pure, so re-do the reload here.
    return reloadRecord(this.store, this.args.application);
  });

  searchApprovers = restartableTask(async (search: string) => {
    if (!search || !this.companyId) {
      return [];
    }
    await timeout(INPUT_DEBOUNCE);
    const { content } = (await this.store.request(
      query<EmployeeModel>('employee', {
        'filter[company_id]': this.companyId as string,
        'filter[search]': search,
      })
    )) as { content: { data: EmployeeModel[] } };
    return content.data.map((employee) => ({
      key: employee.id!,
      label: employee.fullName || employee.id!,
    }));
  });

  fetchApproversTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.searchApprovers,
    () => [this.searchTerm]
  );

  get approverItems() {
    return this.fetchApproversTask.value ?? [];
  }

  confirmReassignment = async () => {
    if (!this.selectedApproverId) return;

    try {
      await this.insertReviewer.perform();
      this.notifications.add('Successfully reassigned approver', {
        appearance: 'success',
      });
      this.cancelReassignment();
      await reloadRecord(this.store, this.args.application);
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  };

  <template>
    <DrawerHeader>
      <h2 class="tio-text-heading tio-fg-primary">
        {{t "tuition_assistance.approval_queue.approval_status"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="-mt-6 mb-6">
        {{#if this.affirmedReviewers.length}}
          <div class="px-6 pt-6 pb-4">
            <h3 class="tio-text-sm-semibold tio-fg-secondary mb-3">Completed Approvals</h3>
            <div class="space-y-2">
              {{#each this.affirmedReviewers as |approver|}}
                <div class="tio-row-between app-status-panel app-status-panel--success p-3">
                  <div class="tio-row">
                    <div class="app-status-avatar app-status-avatar--success text-xs">
                      <span>✓</span>
                    </div>
                    <div>
                      {{#if (isSystemApprover approver.dot)}}
                        <div class="tio-text-sm-medium tio-fg-primary">
                          {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                        </div>
                      {{else}}
                        <div class="tio-text-sm-medium tio-fg-primary">
                          {{this.getApproverName approver.employee_id}}
                        </div>
                        <div class="tio-text-caption">
                          {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                        </div>
                      {{/if}}
                    </div>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
          <div class="border-t app-divider"></div>
        {{/if}}

        <div class="px-6 py-6">
          <h3 class="tio-text-sm-semibold tio-fg-secondary mb-3">Pending Approvers</h3>

          <div class="space-y-6">
            {{#each-in this.pendingReviewersByLevel as |_levelIndex reviewers|}}
              <div class="space-y-3">
                <div class="tio-row-tight">
                  <div class="app-status-avatar app-status-avatar--info text-sm">
                    <span>{{this.getLevelNumber reviewers}}</span>
                  </div>
                  <div class="tio-text-sm-medium tio-fg-secondary">
                    Level
                    {{this.getLevelNumber reviewers}}
                    Approvers -
                    {{reviewers.length}}
                    {{if (eq reviewers.length 1) "approver" "approvers"}}
                    available
                  </div>
                </div>

                <div class="ml-10 space-y-2">
                  {{#each reviewers as |approver|}}
                    <div class="space-y-2">
                      <div
                        class="tio-row-between p-3 tio-surface-sunken border tio-border-default hover:tio-border-strong transition-colors"
                      >
                        <div class="tio-row flex-1">
                          <div class="tio-row-center shrink-0 w-8 h-8 tio-bg-chip rounded-full">
                            <span
                              class="tio-fg-secondary tio-text-xs-semibold"
                            >{{approver.ordinal}}</span>
                          </div>
                          <div class="tio-fill-min">
                            {{#if (isSystemApprover approver.dot)}}
                              <div class="tio-text-sm-medium tio-fg-primary truncate">
                                {{t
                                  (concat "tuition_assistance.approval_queue.roles." approver.dot)
                                }}
                              </div>
                            {{else}}
                              <div class="tio-text-sm-medium tio-fg-primary truncate">
                                {{this.getApproverName approver.employee_id}}
                              </div>
                              <div class="tio-text-caption truncate">
                                {{t
                                  (concat "tuition_assistance.approval_queue.roles." approver.dot)
                                }}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                        <button
                          type="button"
                          class="app-btn-ghost shrink-0 px-3 py-1.5"
                          {{on "click" (fn this.handleReassignClick approver)}}
                        >
                          <PhUserSwitch @size="16" />
                          Reassign
                        </button>
                      </div>

                      {{#if (and (eq this.selectedApprover approver) this.showReassignmentUI)}}
                        <div class="app-status-panel app-status-panel--info mt-2 p-4">
                          <div class="tio-row-between mb-3">
                            <h4 class="tio-text-sm-semibold tio-fg-primary">
                              Reassign
                              {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                            </h4>
                            <button
                              type="button"
                              class="tio-fg-disabled hover:tio-fg-muted"
                              {{on "click" this.cancelReassignment}}
                            >
                              <PhX @size="20" />
                            </button>
                          </div>

                          <Select
                            data-test-approver-select
                            @label={{t
                              "tuition_assistance.approval_queue.select_replacement_approver"
                            }}
                            @searchable={{true}}
                            @isLoading={{this.fetchApproversTask.isRunning}}
                            @selected={{this.selectedApproverId}}
                            @items={{this.approverItems}}
                            @onSearch={{this.onFilterChange}}
                            @onChange={{this.handleSelectionChange}}
                            @clearable={{true}}
                            @placeholder="Search for an employee..."
                            @searchDebounce={{0}}
                          />

                          <div class="tio-flex-tight mt-3">
                            <Button
                              @intent="primary"
                              @size="sm"
                              @isRunning={{this.insertReviewer.isRunning}}
                              {{on "click" this.confirmReassignment}}
                            >
                              Confirm Reassignment
                            </Button>
                            <Button
                              @appearance="outlined"
                              @size="sm"
                              {{on "click" this.cancelReassignment}}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              </div>
            {{/each-in}}
          </div>
        </div>
      </div>
    </DrawerBody>
  </template>
}
